/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import type { ComponentType, SVGProps } from "react"

import GooglePlayLogo from "../../assets/images/logo_google-play.svg?react"
import HuaweiAppGalleryLogo from "../../assets/images/logo_huawei-appgallery.svg?react"
import KoodousLogo from "../../assets/images/logo_koodous.svg?react"
import VirusTotalLogo from "../../assets/images/logo_virustotal.svg?react"
import type { ApkDetail } from "../../features/apk-detail/apk-detail-types"
import type { ExternalLinkGroup, ExternalSource } from "../../features/apk-detail/apk-links"
import { externalLinks } from "../../features/apk-detail/apk-links"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"
import { ExternalLink } from "./external-link"

const GroupHeading = {
    store: "App stores",
    malwareDatabase: "Malware databases"
} as const satisfies Record<ExternalLinkGroup, string>

const SourceLogo = {
    googlePlay: GooglePlayLogo,
    huaweiAppGallery: HuaweiAppGalleryLogo,
    koodous: KoodousLogo,
    virusTotal: VirusTotalLogo
} as const satisfies Record<ExternalSource, ComponentType<SVGProps<SVGSVGElement>>>

interface Props {
    /**
     * APK identifiers used to build the external links.
     *
     * Stores search by package name; Koodous also uses the APK hash, and
     * VirusTotal uses the hash alone.
     */
    apk: Pick<ApkDetail, "packageName" | "apkSignature">
}

/**
 * Links to external stores and malware databases for the APK, grouped by kind,
 * each led by its source's logo.
 *
 * @example
 * <ApkExternalLinks apk={apk} />
 */
export const ApkExternalLinks = observer(({ apk }: Props) => (
    <Card>
        <CardHeader>
            <CardTitle>
                <h3 className="m-0 text-xl font-bold uppercase">External Sources</h3>
            </CardTitle>
        </CardHeader>
        <CardContent className="gap-4">
            {Object.entries(GroupHeading).map(([group, heading]) => (
                <div key={group} className="grid gap-2">
                    <h4 className="m-0 text-xs font-semibold text-muted-foreground uppercase">{heading}</h4>
                    {/* Preserve list semantics in Safari when the list has no default styling. */}
                    <ul role="list" className="m-0 flex list-none flex-col items-start gap-2 p-0">
                        {externalLinks(apk)
                            .filter((link) => link.group === group)
                            .map(({ label, href, source }) => (
                                <li key={label}>
                                    <ExternalLink href={href} icon={SourceLogo[source]}>
                                        {label}
                                    </ExternalLink>
                                </li>
                            ))}
                    </ul>
                </div>
            ))}
        </CardContent>
    </Card>
))
